'use client';
import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import useDeviceDetection from '@hooks/useDeviceDetection';
import useModalManager from '@hooks/useModalManager';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { pushAlert } from 'redux/actions/alertActions';
import { login, loginWithToken } from 'redux/actions/authActions';
import { resetModal } from 'redux/actions/modalActions';
import AppLogin from './AppLogin';
import { Forgot } from './Forgot';

const AccountBlockedInfo = dynamic(() => import('./AccountBlockedInfo'));

interface Props {
    redirect?: string;
    registerCallback?: () => void;
    disableAutoClose?: true;
    withReload?: boolean;
}

export const Login = ({ redirect, registerCallback, disableAutoClose, withReload = true }: Props) => {
    const device = useDeviceDetection();
    const [isLoading, setIsLoading] = useState(false);
    const dispatch = useDispatch();
    const [appLoginSuccess, setAppLoginSuccess] = useState(false);
    const router = useRouter();
    const { t } = useTranslation('auth');
    const { resetModals, addModal, removeModal } = useModalManager();

    const handleForgotten = () =>
        addModal({
            title: t('Passwort zurücksetzen'),
            content: (id) => <Forgot callback={() => removeModal(id)} />,
            show: true,
            size: 40
        });

    const closeModals = () => {
        resetModals();
        dispatch(resetModal());
    };
    const handleSubmit = (data) => {
        setIsLoading(true);
        dispatch(
            login(data.username, data.password, {
                success: () => {
                    if (!disableAutoClose) closeModals();

                    if (redirect) {
                        router.push(redirect);

                        closeModals();
                    }

                    if (withReload) {
                        router.refresh();
                        closeModals();
                    }
                },
                failure: (message, data) => {
                    setIsLoading(false);
                    if (data?.blocked) {
                        addModal({
                            content: (id) => <AccountBlockedInfo />
                        });
                    } else
                        dispatch(
                            pushAlert(
                                message ||
                                    t('Der Login war leider nicht erfolgreich! Benutzername oder Passwort falsch!'),
                                'danger'
                            )
                        );
                }
            })
        );
    };

    return (
        <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-[1fr,auto,1fr]">
            {device === 'desktop' && (
                <>
                    <AppLogin
                        className="tw-border-0"
                        disableRefresh={appLoginSuccess}
                        onRefresh={() => setAppLoginSuccess(false)}
                        onAuthentication={(token, user) => {
                            dispatch(loginWithToken(token, user));
                            setAppLoginSuccess(true);
                            resetModals();
                            router.refresh();
                        }}
                    />
                    <div className="tw-my-4 tw-flex tw-flex-col tw-items-center tw-gap-4">
                        <div className="tw-flex-1 tw-border-0 tw-border-r tw-border-solid tw-border-accents-2" />
                        <span className="tw-italic tw-text-accents-6">oder</span>
                        <div className="tw-flex-1 tw-border-0 tw-border-r tw-border-solid tw-border-accents-2" />
                    </div>
                </>
            )}
            <div className="tw-self-center">
                <Form onSubmit={handleSubmit}>
                    <FormInput name="username" label={t('E-Mail Adresse')} required autoFocus />
                    <FormInput type="password" name="password" label={t('Passwort')} required />
                    <FormInput.Submit isLoading={isLoading} disabled={appLoginSuccess}>
                        <Trans ns="auth">Einloggen</Trans>
                    </FormInput.Submit>
                </Form>
                <button disabled={appLoginSuccess} type="button" className="btn btn-link" onClick={handleForgotten}>
                    <Trans ns="auth">Passwort vergessen?</Trans>
                </button>
                {registerCallback && (
                    <>
                        <hr className="my-3" />
                        <p className="text-center">
                            <Trans ns="auth">Noch kein Account?</Trans>
                            <button
                                disabled={appLoginSuccess}
                                type="button"
                                className="btn btn-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (registerCallback) {
                                        registerCallback();
                                    } else {
                                        router.push('/register');
                                    }
                                }}
                            >
                                <Trans ns="auth">Jetzt registrieren</Trans>
                            </button>
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};
